import { CHAT_API_BASE_URL } from "./chatService";
import { notifySessionUnauthorized } from "../auth/sessionUnauthorizedEvents";
import { authedFetch } from "../auth/authedFetch";
import { debug } from "../utils/debugLogger";

// Conversation history interfaces
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  /** Assistant message types from API */
  type?: 'action' | 'action_result' | 'research_document' | 'research_pending' | 'image_batch_pending' | 'blog' | 'outreach_email' | 'calls' | 'card_contacts' | 'card_details' | 'gbp_proposal';
  action_name?: string;
  image_urls?: string[];
  /** Structured blog payload (type === 'blog') from the output_blog tool */
  blog?: Record<string, any>;
  /** Structured outreach email draft (type === 'outreach_email') from the output_outreach_email tool */
  outreach_email?: Record<string, any>;
  /** action_result with social posts (array of { platform, title, post, image_url, hashtags, call_to_action }) */
  posts?: any[];
  /** Deep-research report payload (type === 'research_document'); url is re-signed by backend on load */
  research_document?: Record<string, any>;
  /** Epoch ms a still-running deep research started (type === 'research_pending') */
  research_started_at?: number;
  /** Epoch ms a still-running bulk image batch started (type === 'image_batch_pending') */
  image_batch_started_at?: number;
  /** GHL call list (type === 'calls') from the output_calls tool */
  calls?: Record<string, any>;
  /** Scanned cards + their drafts (type === 'card_contacts'), SGL-346 */
  card_contacts?: Record<string, any>;
  /** Just-scanned cards awaiting correction (type === 'card_details'), SGL-346 */
  card_details?: Record<string, any>;
  /** The Visibility Manager's Business Profile card (type === 'gbp_proposal'), SGL-328 */
  gbp_proposal?: Record<string, any>;
  /** The stored row's DynamoDB sort key, for a card that has to be amended later. */
  sk?: string;
  has_image?: boolean;
}

/** Structured payloads copied straight off a stored row. Anything not named here
 *  is dropped between DynamoDB and the renderer — see the note at the copy site. */
const STRUCTURED_FIELDS = [
  "blog",
  "outreach_email",
  "research_document",
  "calls",
  "card_contacts",
  "card_details",
  "gbp_proposal",
] as const;

export interface ConversationThread {
  threadId: string;
  conversationId?: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  lastMessageAt: number;
  messageCount: number;
  status: 'active' | 'archived';
  agentId?: string;
  customerId?: string;
  preview?: string; // Last message preview
  messages: ChatMessage[]; // Store actual messages
  /** A deep research is still running for this thread (drives the sidebar pending dot). */
  researchPending?: boolean;
  /** A bulk image batch is still running for this thread (drives the sidebar pending dot). */
  imageBatchPending?: boolean;
}

export interface ConversationHistoryResponse {
  threads: ConversationThread[];
  hasMore: boolean;
  lastKey?: string;
}

// Backend conversation response interface
interface BackendConversation {
  thread_id: string;
  conversation_id: string;
  title?: string;
  agent_id: string;
  customer_id: string;
  user_id: string;
  created_at: number; // milliseconds
  last_message_at: number; // milliseconds
  last_message_tokens: number;
  cumulative_tokens: number;
  status: "active" | "archived";
  research_pending?: boolean;
  image_batch_pending?: boolean;
}

// Backend API response wrapper
interface BackendConversationsResponse {
  items: BackendConversation[];
  next_cursor: string | null;
}

/**
 * Fetch conversation history for a user (Backend API)
 * @param customerId - Customer ID (not used in API call, kept for compatibility)
 * @param idToken - User's authentication token (backend extracts user from token)
 * @param limit - Number of threads to fetch (default: 20)
 * @param lastKey - Pagination key for fetching more results
 * @param agentId - When set, restrict to threads for this agent (query param + client filter fallback)
 * @returns Promise<ConversationHistoryResponse>
 */
export const fetchConversationHistory = async (
  customerId: string,
  idToken: string,
  limit: number = 20,
  lastKey?: string,
  agentId?: string | null
): Promise<ConversationHistoryResponse> => {
  try {
    if (!CHAT_API_BASE_URL) {
      debug.error("Chat API base URL is not configured");
      throw new Error("Chat API base URL is not configured");
    }

    const params = new URLSearchParams({
      limit: limit.toString(),
      status: "active",
    });
    if (agentId) {
      params.set("agent_id", agentId);
    }
    if (customerId) {
      params.set("customer_id", customerId);
    }

    const url = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/conversations?${params.toString()}`;
    
    const response = await authedFetch(
      url,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      idToken
    );

    if (!response.ok) {
      notifySessionUnauthorized(response.status);
      throw new Error(`Failed to fetch conversations: ${response.statusText}`);
    }

    const responseData: BackendConversationsResponse = await response.json();

    const mappedThreads = responseData.items.map((conv) => ({
        threadId: conv.thread_id,
        conversationId: conv.conversation_id,
        title: conv.title || "Untitled Conversation",
        createdAt: Math.floor(conv.created_at / 1000),
        updatedAt: Math.floor(conv.last_message_at / 1000),
        lastMessageAt: Math.floor(conv.last_message_at / 1000),
        messageCount: 0, // Phase 1: Not available
        status: conv.status,
        agentId: conv.agent_id,
        customerId: conv.customer_id,
        preview: "", // Phase 1: Not available
        messages: [], // Phase 1: Empty (OpenAI stores them)
        researchPending: conv.research_pending === true,
        imageBatchPending: conv.image_batch_pending === true,
      }));

    let threads =
      agentId != null && agentId !== ""
        ? mappedThreads.filter((t) => t.agentId === agentId)
        : mappedThreads;

    if (customerId) {
      threads = threads.filter((t) => t.customerId === customerId);
    }

    return {
      threads,
      hasMore: responseData.next_cursor !== null,
      lastKey: responseData.next_cursor || undefined,
    };
  } catch (error) {
    debug.error("Error fetching conversation history:", error);
    throw error;
  }
};

/**
 * Fetch specific conversation thread details (Backend API)
 * @param threadId - The thread ID to fetch
 * @param idToken - User's authentication token
 * @returns Promise<ConversationThread>
 */
export const fetchConversationThread = async (
  threadId: string,
  idToken: string
): Promise<ConversationThread> => {
  try {
    if (!CHAT_API_BASE_URL) {
      debug.error("Chat API base URL is not configured");
      throw new Error("Chat API base URL is not configured");
    }

    const url = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/conversations/${threadId}`;
    
    const response = await authedFetch(
      url,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      idToken
    );

    if (!response.ok) {
      notifySessionUnauthorized(response.status);
      throw new Error(`Failed to fetch conversation: ${response.statusText}`);
    }

    const conv: BackendConversation = await response.json();

    return {
      threadId: conv.thread_id,
      conversationId: conv.conversation_id,
      title: conv.title || "Untitled Conversation",
      createdAt: Math.floor(conv.created_at / 1000),
      updatedAt: Math.floor(conv.last_message_at / 1000),
      lastMessageAt: Math.floor(conv.last_message_at / 1000),
      messageCount: 0, // Phase 1: Not available
      status: conv.status,
      agentId: conv.agent_id,
      customerId: conv.customer_id,
      preview: "", // Phase 1: Not available
      messages: [], // Phase 1: Empty (OpenAI stores them)
    };
  } catch (error) {
    debug.error("Error fetching conversation thread:", error);
    throw error;
  }
};

/**
 * Archive a conversation thread (Stub - Phase 2)
 * @param threadId - The thread ID to archive
 * @param idToken - User's authentication token
 * @returns Promise<void>
 */
export const archiveConversationThread = async (
  threadId: string,
  idToken: string
): Promise<void> => {
  // Phase 2: Will call backend API to archive
  throw new Error("Archive conversation not yet implemented");
};

/**
 * Delete a conversation thread (Stub - Phase 2)
 * @param threadId - The thread ID to delete
 * @param idToken - User's authentication token
 * @returns Promise<void>
 */
export const deleteConversationThread = async (
  threadId: string,
  idToken: string
): Promise<void> => {
  if (!CHAT_API_BASE_URL) {
    debug.error("Chat API base URL is not configured");
    throw new Error("Chat API base URL is not configured");
  }

  const url = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/conversations/${threadId}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    notifySessionUnauthorized(response.status);
    throw new Error(`Failed to delete conversation: ${response.statusText}`);
  }
};

export interface ResearchStatusResponse {
  status: 'in_progress' | 'completed' | 'failed' | 'none';
  research_document?: Record<string, any> | null;
  started_at?: number | null;
  elapsed_ms?: number;
}

/**
 * Poll the status of a deep-research job on a thread.
 * Returns status and, when completed, the research document with re-signed S3 URLs.
 */
export const pollResearchStatus = async (
  threadId: string,
  idToken: string,
): Promise<ResearchStatusResponse> => {
  if (!CHAT_API_BASE_URL) throw new Error("Chat API base URL is not configured");
  const url = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/research-status/${threadId}`;
  const response = await authedFetch(url, { method: "GET", headers: { "Content-Type": "application/json" } }, idToken);
  if (!response.ok) {
    notifySessionUnauthorized(response.status);
    throw new Error(`Failed to poll research status: ${response.statusText}`);
  }
  return response.json();
};

export interface ImageBatchStatusResponse {
  status: 'in_progress' | 'completed' | 'failed' | 'none';
  image_urls?: string[] | null;
  started_at?: number | null;
  elapsed_ms?: number;
}

/**
 * Poll the status of a bulk image batch (generate_image_batch) job on a thread.
 * Returns status and, when completed, the generated image URLs.
 */
export const pollImageBatchStatus = async (
  threadId: string,
  idToken: string,
): Promise<ImageBatchStatusResponse> => {
  if (!CHAT_API_BASE_URL) throw new Error("Chat API base URL is not configured");
  const url = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/image-batch-status/${threadId}`;
  const response = await authedFetch(url, { method: "GET", headers: { "Content-Type": "application/json" } }, idToken);
  if (!response.ok) {
    notifySessionUnauthorized(response.status);
    throw new Error(`Failed to poll image batch status: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch messages for a specific conversation
 * @param threadId - The thread ID
 * @param idToken - User's authentication token
 * @returns Promise<ChatMessage[]>
 */
export const fetchConversationMessages = async (
  threadId: string,
  idToken: string
): Promise<ChatMessage[]> => {
  try {
    if (!CHAT_API_BASE_URL) {
      debug.error("Chat API base URL is not configured");
      throw new Error("Chat API base URL is not configured");
    }

    const url = `${CHAT_API_BASE_URL.replace(/\/$/, "")}/chat/conversations/${threadId}/messages`;
    
    const response = await authedFetch(
      url,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      idToken
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      notifySessionUnauthorized(response.status);
      throw new Error(`Failed to fetch message history: ${response.statusText}`);
    }

    const data = await response.json();

    // Backend returns: { thread_id, conversation_id, messages: [...], count }
    if (data.messages && Array.isArray(data.messages)) {
      return data.messages.map((msg: any, index: number) => {
        const base: ChatMessage = {
          id: `msg_${data.thread_id}_${index}`,
          role: msg.role,
          content: msg.content ?? '',
          timestamp: Math.floor(Date.now() / 1000),
        };
        if (msg.type != null) base.type = msg.type;
        if (msg.action_name != null) base.action_name = msg.action_name;
        if (Array.isArray(msg.image_urls)) base.image_urls = msg.image_urls;
        if (Array.isArray(msg.posts)) base.posts = msg.posts;
        // One entry per structured payload, rather than a line each: a type added to
        // the backend and forgotten here is a card that silently vanishes on reload —
        // the row is in DynamoDB and historyMessageMapper knows how to restore it, but
        // the payload never arrives. That bit `calls` and then `card_contacts`
        // (confirmed live 2026-09-25, a scanned-cards turn came back as an empty
        // assistant bubble). Add the name here when you add the type.
        STRUCTURED_FIELDS.forEach((field) => {
          if (msg[field] != null) (base as any)[field] = msg[field];
        });
        // The row's own sort key. A restored card needs it to name the turn it is
        // settling, so approving one that was reopened can still mark it saved.
        if (typeof msg.sk === "string") base.sk = msg.sk;
        if (typeof msg.research_started_at === "number") base.research_started_at = msg.research_started_at;
        if (typeof msg.image_batch_started_at === "number") base.image_batch_started_at = msg.image_batch_started_at;
        if (msg.has_image === true) base.has_image = true;
        return base;
      });
    }

    return [];
  } catch (error) {
    debug.error("Error fetching message history:", error);
    // Don't throw - return empty array to allow conversation to continue
    return [];
  }
};
